import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteTask, fetchTasks } from "../redux/actions/taskActions";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../utils/axiosConfig";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  TablePagination,
  Container,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { smallTitle } from "../styles/typography";
import { CircularProgress } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { formatDate } from "../utils/dateUtils";

const tableStyles = windowsTableStyles();

const TasksTable = () => {
  const { t } = useTranslation(['common', 'buttons']);

  const calculateTimeLeft = (dueDate) => {
    const due = new Date(dueDate);
    const now = new Date();
    const timeDifference = due - now;

    const daysLeft = Math.ceil(timeDifference / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
    return daysLeft > 0 ? t('common:dateTime.daysLeft', { count: daysLeft }) : t('common:dateTime.pastDue');
  };
  const { id } = useParams(); // Assuming the case ID is passed as a URL parameter
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Use local state for case-specific tasks to avoid conflicts with global list
  const [caseTasks, setCaseTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Fetch tasks directly using axios to avoid Redux conflicts
  useEffect(() => {
    const fetchCaseTasks = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/tasks/case/${id}`);
        setCaseTasks(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCaseTasks();
    }
  }, [id]);

  const handleDeleteClick = (taskId, e) => {
    e.stopPropagation();
    setTaskToDelete(taskId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (taskToDelete) {
      try {
        await dispatch(deleteTask(taskToDelete));
        setTimeout(async () => {
          try {
            const response = await axios.get(`/api/tasks/case/${id}`);
            setCaseTasks(response.data);
          } catch (err) {
            console.error('Error refetching tasks:', err);
          }
        }, 300);
      } catch (error) {
        console.error('Error deleting task:', error);
        alert(error?.message || 'Failed to delete task. Please try again.');
      }
    }
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewTask = (taskId) => {
    const currentFile = files.find((file) => file._id === id);
    const owner = currentFile ? currentFile.officeMembers[0].member : null;
    navigate(`/task/${taskId}`, { state: { caseId: id, owner } });
  };

  const userFiles = files.filter((file) => {
    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (user.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });
  
  const [filteredTasks, setFilteredTasks] = useState([]);

  useEffect(() => {
    if (caseTasks && caseTasks.length > 0) {
      const filtered = caseTasks.filter((task) =>
        id === task.case?._id
      );
      setFilteredTasks(filtered);
    } else {
      setFilteredTasks([]);
    }
  }, [caseTasks, id]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const stringToColor = (string) => {
    if (!string) return "#ccc"; // Default gray color if string is empty

    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }

    return color;
  };

  const getColor = (color) => {
    switch (color) {
      case "Red":
        return "#e5202e";  // Blue for In Progress
      case "Blue":
        return "Blue";  // Gray for Not Started
      case "Green":
        return "#85bc20";  // Green for Completed
      case "Yellow":
        return "Yellow";
      default:
        return "transparent";  // Transparent if no status
    }
  };


  return (
    <Box sx={{ backgroundColor: "white", padding: "32px 24px " }}>
      <TableContainer>
        {filteredTasks.length > 0 ? (
          <Table
            sx={{ ...tableStyles.container, border: "1px solid #d1d1d1" }}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.dueDate')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('common:timeLeft')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.subject')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.owner')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.type')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('common:client')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('common:actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((task) => (
                  <TableRow
                    key={task._id}
                    onMouseEnter={() => setHoveredTaskId(task._id)}
                    onMouseLeave={() => setHoveredTaskId(null)}
                    sx={{ ...tableStyles.row, "&:hover": { backgroundColor: "#ecf5fb" } }}
                  >
                    <TableCell sx={tableStyles.cell}>
                      {formatDate(task.dueDate)}
                    </TableCell>
                    <TableCell sx={tableStyles.cell}>
                      {calculateTimeLeft(task.dueDate)}
                    </TableCell>
                    <TableCell sx={tableStyles.cell}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            height: "10px",
                            width: "10px",
                            backgroundColor: getColor(task?.colour),
                            marginRight:"-2px"
                          }}
                        />
                        <span>{task.subject}</span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => { navigate(`/relationships/${task?.owner?._id}`); }}>
                      {task.owner ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar
                            alt={task.owner.firstName}
                            src={task.owner.avatar ? task.owner.avatar : ""}
                            sx={{
                              fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                              fontSize: "7.8px",
                              height: "1rem",
                              width: "1rem",
                              backgroundColor: task.owner.avatar
                                ? "transparent"
                                : stringToColor(task.owner.firstName), // Ensures same color for same name
                              color: "#fff",
                            }}
                          >
                            {!task.owner.avatar &&
                              `${task.owner.firstName.charAt(0)}${task.owner.middleName ? task.owner.middleName.charAt(0) : task.owner.lastName.charAt(0)}`}
                          </Avatar>
                          {`${task.owner.firstName} ${task.owner.middleName ? task.owner.middleName + " " : ""}${task.owner.lastName}`}
                        </Box>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>

                    <TableCell sx={{ ...tableStyles.cell, "&:hover": { textDecoration: "underline", cursor: "pointer" }, color: "#005b92", }} onClick={() => handleViewTask(task._id)}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FormatListNumberedIcon sx={{ fontSize: 16, color: "gray" }} />
                        <span>{task.type}</span>
                      </Box>
                    </TableCell>

                    <TableCell
                          sx={{
                            ...tableStyles.cell,
                            cursor: "pointer",
                          }}
                        >
                          {task.case?.parties?.find(p => p.type === "Client") ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                alt={
                                  task.case.parties.find((party) => party.type === "Client")
                                    ?.name?.businessName || "N/A"
                                }
                                src={
                                  task.case.parties.find((party) => party.type === "Client")
                                    ?.name?.avatar || "/static/images/avatar/1.jpg"
                                }
                                sx={{
                                  fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                  fontSize: "7.8px",
                                  height: "1rem",
                                  width: "1rem",
                                  marginRight: "-2px",
                                }}
                              />
                              <Box
                                sx={{
                                  
                                  lineHeight: "14px",
                                }}
                              >
                                {(() => {
                                  const client = task.case.parties.find(
                                    (party) => party.type === "Client"
                                  )?.name;
                                  return client
                                    ? client.businessName ||
                                    `${client.firstName || ""} ${client.lastName || ""}`.trim()
                                    : "N/A";
                                })()}
                              </Box>
                            </Box>
                          ) : (
                            "--"
                          )}
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>
                          {hoveredTaskId === task._id && (
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(task._id, e)}
                              sx={{
                                padding: "2px",
                                color: "rgb(229, 32, 46)",
                                "&:hover": {
                                  backgroundColor: "rgba(229, 32, 46, 0.1)",
                                },
                              }}
                            >
                              <DeleteOutline sx={{ fontSize: "16px" }} />
                            </IconButton>
                          )}
                        </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        ) : (
          <Box
            sx={{ ...smallTitle, padding: "32px 24px", textAlign: "center" }}
          >
            {t('common:no_data_available')}
          </Box>
        )}
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredTasks.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('common:confirm_delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('common:confirm_delete_task')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('buttons:secondary.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            {t('buttons:danger.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TasksTable;
